import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Shield, Search, Puzzle, Handshake } from "lucide-react";

const reasons = [
  { icon: Shield, title: "Independent Assessments", desc: "Objective reviews focused on what truly matters for food safety and operational hygiene." },
  { icon: Search, title: "Thorough Walkthroughs", desc: "Structured site visits that look at food handling, sanitation, storage and overall readiness." },
  { icon: Puzzle, title: "Practical Solutions", desc: "Clear, implementable guidance that outlet teams can actually follow and sustain." },
  { icon: Handshake, title: "Supportive Approach", desc: "A partner-led working style that helps businesses improve without unnecessary complexity." },
];

export default function WhyUs() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="why" className="py-12 md:py-20" data-testid="why-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#7CA63A] mb-4 block">Why Us</span>
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl font-bold text-[#16231A] tracking-tight leading-[1.2] mb-4">
            Why Choose Us
          </h2>
          <p className="text-[#526B57] max-w-2xl mx-auto leading-relaxed">
            We combine food operations understanding with a practical audit mindset so businesses get useful observations, not generic paperwork.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {reasons.map((r, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 25 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 * i }}
              className="bg-white border border-[#E3E8DC] rounded-2xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-all duration-300 text-center group"
              data-testid={`why-card-${i}`}
            >
              <div className="w-14 h-14 bg-[#EAF0E2] rounded-2xl flex items-center justify-center mx-auto mb-5 group-hover:bg-[#7CA63A] transition-colors duration-300">
                <r.icon size={24} className="text-[#7CA63A] group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="font-['Outfit'] text-lg font-bold text-[#16231A] mb-2">{r.title}</h3>
              <p className="text-sm text-[#526B57] leading-relaxed">{r.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
